import { Address, RoundingMethod, TaxAggMethod } from "~/entities/siteEntities";
import { DiscountType, Promotion } from "~/entities/promotionEntities";
import { Product } from "~/entities/productEntities";
import { TaxInfo } from "~/entities/taxEntities";
import { v4 } from "uuid";

export enum OrderSource {
  ONLINE = "ONLINE",
  POS = "POS",
  KIOSK = "KIOSK",
  MOBILE = "MOBILE",
  TABLE_ORDERING = "TABLE_ORDERING",
  // CATERING is its own customer-facing experience, like table ordering — its
  // own /catering route, its own order types, and its own menu. Order types
  // live under this source instead of being lumped under ONLINE.
  CATERING = "CATERING",
}

export enum GlobalOrderTypeCode {
  POS_IN_STORE = "POS_IN_STORE",
  POS_COLLECTION = "POS_COLLECTION",
  POS_DELIVERY = "POS_DELIVERY",
  TABLE_ORDERING_IN_STORE = "TABLE_ORDERING_IN_STORE",
  ONLINE_COLLECTION = "ONLINE_COLLECTION",
}

export enum OrderStatus {
  EMPTY = "EMPTY",
  DRAFT = "DRAFT",
  HELD = "HELD",
  SENT = "SENT",
  VOIDED = "VOIDED",
  CANCELLED = "CANCELLED",
}

export enum OrderFulfillmentType {
  IN_STORE = "IN_STORE", // e.g. dine in
  COLLECTION = "COLLECTION", // e.g. pickup or takeaway
  DELIVERY = "DELIVERY",
}

export interface OrderType {
  id: string;
  code: string;
  name: string;
  active: boolean;
  source: OrderSource;
  fulfillmentType: OrderFulfillmentType;
}

// REQUEST STRUCTURES

export interface RequestOrder {
  tableNumber: string | null;
  orderTypeCode: string;
  requestedReadyTime: string | null;
  customer: Customer;
  redemptionKey: string | null;
  promoCode: string | null;
  groupOrder: boolean;
  deliveryInfo: DeliveryInfo | null;
}

export interface DeliveryInfo {
  address: Address;
  uberDirect?: UberDirectInfo;
}

// Uber Direct courier info surfaced back on the order. trackingUrl links to
// Uber's live tracking page; courier is populated once one is assigned.
export interface UberDirectInfo {
  quoteId?: string;
  deliveryId?: string;
  trackingUrl?: string;
  dropoffEta?: string; // ISO timestamp
  courier?: UberDirectCourier;
}

export interface UberDirectCourier {
  name?: string;
  phoneNumber?: string;
  vehicleType?: string;
  imageUrl?: string;
  rating?: number;
  latitude?: number;
  longitude?: number;
}

export interface PlaceOrderRequest extends RequestOrder {
  lines: RequestLine[];
  isLogout: boolean;
}

export interface Customer {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export interface BaseOrderLine {
  id: string;
  menuCode: string;
  menuItemCode: string | null;
}
export interface RequestLine extends BaseOrderLine {
  item: RequestLineItem;
}

export interface BaseLineItem {
  id: string;
  qty: number;
  productCode: string;
  sku: string;
}
export interface RequestLineItem extends BaseLineItem {
  children: RequestLineItem[];
}

// RESPONSE STRUCTURES
export interface ResponseOrder extends RequestOrder {
  id: string;
  taxAggregationMethod: TaxAggMethod;
  taxRoundingMethod: RoundingMethod;
  orderNumber: string;
  pricesIncludeTax: boolean;
  lineTotalBeforeDiscount: number;
  totalBeforeDiscount: number;
  finalPriceExTax: number;
  finalPriceIncTax: number;
  discounts: Discount[];
  additionalCharges: Charge[];
  promotionSchedulingOverride: Record<string, boolean>;
  status: OrderStatus;
  estimatedReadyTime: string | null;

  // Not in db object
  paidAmount: number;
  pendingPaymentIds: string[];
  unlockedPromotions: Promotion[];
  redemptionName: string | null;
  // Server-computed BUNDLE_PRICE completion suggestions. Optional because
  // backends predating bundles do not send it.
  bundleUpsells?: BundleUpsell[];
}

// One server-computed "complete this bundle" suggestion (mirrors the Go
// helpers.BundleUpsell shape carried on the order response beside
// unlockedPromotions). Lives here rather than promotionEntities because it is
// a response-order DTO, not part of the authored promotion schema.
export interface BundleUpsell {
  promotionCode: string;
  name: string;
  missingProductCodes: string[];
  completionCost: number;
  saving: number;
}
export interface ResponseOrderWithLines extends ResponseOrder {
  lines: ResponseLine[];
  customerId: string | null;
}

export interface ResponseOrderWithSimpleOrderLines extends ResponseOrder {
  orgId: string;
  siteId: string;
  siteName: string;
  paidTime: string | null;
  orderLines: SimpleOrderLine[];
}

export interface SimpleOrderLine {
  id: string;
  item: SimpleItem;
  menuCode: string;
}

export interface SimpleItem {
  name: string;
  qty: number;
  price: number;
  hide: boolean;
  children: SimpleItem[];
}

export interface ResponseLine extends BaseOrderLine {
  item: ResponseLineItem;
  discounts: Discount[];
}

export interface BaseResponseLineItem extends BaseLineItem {
  displayName: string;
  unitPrice: number;
  finalUnitPrice: number;
  hide: boolean;
  discounts: Discount[];
}
export interface ResponseLineItem extends BaseResponseLineItem {
  children: ResponseLineItem[];
}

export interface Discount {
  id: string;
  name: string;
  promotionCode: string;
  percentTimes1000: number;
  fixedAmount: number;
  // Response-only marker resolved server-side from the promotions list
  // ("BUNDLE_PRICE" records drive the aggregated deal row in the cart).
  // Absent/empty for manual (Como) records and client-created records.
  discountType?: string;
}

export interface Charge {
  amount: number;
  finalAmount: number;
  name: string;
  taxes: TaxInfo[];
}

export interface ResponsePromotion {
  code: string;
  name: string;

  // Promotion requirements (see also schedules)
  orderTypeCodes: string[];
  requiredProductCodes: string[];
  requiredQty: number;
  requiredPrice: number;

  // Promotion reward specifications
  discountType: DiscountType;
  productCodes: string[];
  fixedAmount: number;
  percentTimes1000: number;
  maxUsesPerOrder: null | number;
  maxUsesPerRequirements: null | number;
}

// CART STRUCTURES
export interface CartLine extends BaseOrderLine {
  item: CartLineItem;
  discounts: Discount[];
}
export interface CartLineItem extends BaseResponseLineItem {
  children: CartLineItem[];
  product: Product;
}

export interface ResponseOrderWithCartLines extends ResponseOrder {
  lines: CartLine[];
}

// FUNCTIONS

// Build the order payload for a fulfilment/order-type change on an EXISTING
// order. The new orderTypeCode MUST be forced onto the order: the cart summary
// carries the persisted order's type, which is stale once the shopper switches
// fulfilment (e.g. Pickup -> Delivery). Without this, the order-update PUT would
// re-send the old type and the order would never become a delivery order (so no
// delivery charge). See OrderContext.setOrderTypeInfo.
export const applyOrderTypeChange = (
  order: ResponseOrderWithCartLines,
  newOrderTypeCode: string,
  requestedReadyTime: string | null,
  deliveryInfo: DeliveryInfo | null
): ResponseOrderWithCartLines => ({
  ...order,
  orderTypeCode: newOrderTypeCode,
  requestedReadyTime,
  deliveryInfo,
});

// deliveryAddressKey is the comparison key the reprice sync uses to detect a
// changed delivery. It keys on the address only, so backend-managed fields
// (uberDirect quote, pickup/delivery instructions) that the session's
// deliveryInfo never carries don't make the comparison perpetually unequal and
// re-fire a spurious order-update.
export const deliveryAddressKey = (info: DeliveryInfo | null): string =>
  JSON.stringify(info?.address ?? null);

// A persisted order can only be edited while it is a DRAFT. Once it's placed
// (SENT/HELD/etc.) any update PUT is rejected by the backend with
// INVALID_ORDER_STATUS, so the cart must be treated as read-only. A missing
// status means a brand-new order, which is editable.
export const isOrderEditable = (status?: OrderStatus): boolean =>
  !status || status === OrderStatus.DRAFT;

export const cartToReq = (
  x: ResponseOrderWithCartLines,
  isLogout?: boolean,
): PlaceOrderRequest => ({
  tableNumber: x.tableNumber,
  orderTypeCode: x.orderTypeCode,
  requestedReadyTime: x.requestedReadyTime,
  customer: x.customer,
  redemptionKey: x.redemptionKey,
  promoCode: x.promoCode,
  groupOrder: x.groupOrder,
  lines: x.lines.map((l) => ({ ...l, item: cartToReqItem(l.item) })),
  isLogout: !!isLogout,
  deliveryInfo: x.deliveryInfo,
});

const cartToReqItem = (x: CartLineItem): RequestLineItem => ({
  id: x.id,
  qty: x.qty,
  productCode: x.productCode,
  sku: x.sku,
  children: x.children.map((c) => cartToReqItem(c)),
});

export const duplicateLineWithoutDiscounts = (line: CartLine): CartLine => ({
  ...line,
  id: v4(),
  discounts: [],
  item: duplicateItemWithoutDiscounts(line.item),
});

export const duplicateItemWithoutDiscounts = (
  item: CartLineItem,
): CartLineItem => ({
  ...item,
  id: v4(),
  discounts: [],
  children: item.children.map((x) => duplicateItemWithoutDiscounts(x)),
});
